import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SocialIconButton from "../components/SocialIconButton";
import { FaLinkedin, FaInstagram, FaGithub, FaStackOverflow } from "react-icons/fa";
import { socialMediaLinks, logoBackPath, loginName, telNummer, mail } from "../config";
import UnderlineSketch from "./UnderlineSketch";
import { useAuth } from "../auth/AuthContext";
import { MonoButtonSmall, PaperGradientBG } from "../kits/DesignKit";

import { useCourses } from "../hooks/useCourses";
import { useBlogPosts } from "../hooks/useBlogPosts";
import SearchOverlay from "../components/SearchOverlay";

import { MonoButton } from "../kits";

const BookingModal = lazy(() => import("./BookingModal"));

// Sekundär-Navigation für verschiedene Seiten
const pageAnchors = {
  // About-Navigation wurde ins UserOverlay verschoben
  // "/about": {
  //   tabs: [
  //     { id: "overview", label: "Übersicht" },
  //     { id: "kompass", label: "Kompass" },
  //     { id: "referenzen", label: "Referenzen" }
  //   ],
  //   useQuery: false // verwendet #anchor
  // }
};

function Navbar({ updateNavbarHeight }) {
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
 
  const location = useLocation();
  const navbarRef = useRef(null);
  const scrollPosition = useRef(0);

  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [, setShowTopBar] = useState(true);

  const { courses } = useCourses();
  const { blogPosts } = useBlogPosts();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ courses: [], blog: [] });
  const [, setShowSearchResults] = useState(false);
  const [isDesktopSearchOverlayOpen, setIsDesktopSearchOverlayOpen] = useState(false);
  const [searchOverlayInitialView, setSearchOverlayInitialView] = useState("search");

  const lastScrollY = useRef(0);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingContext, setBookingContext] = useState("audit");
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);

  const scrollToAnalysis = useCallback(() => {
    const performScroll = () => {
      const target = document.getElementById("analysis-entry") || document.getElementById("analysis");
      if (!target) return;
      const navHeight = navbarRef.current?.offsetHeight || 120;
      const y = target.getBoundingClientRect().top + window.scrollY - navHeight - 10;
      window.scrollTo({ top: y, behavior: "smooth" });
    };

    window.dispatchEvent(new CustomEvent("open-strategic-analysis"));

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-strategic-analysis"));
        performScroll();
      }, 280);
      setTimeout(performScroll, 720);
      return;
    }

    setTimeout(performScroll, 80);
    setTimeout(performScroll, 540);
  }, [location.pathname, navigate]);

  const navLinks = [
    { name: "Moin ..", path: "/", isGreeting: true },
    { name: "Ich", path: "/about" },
    { name: "ZenApp", path: "/zenapp" },
    { name: "ZenDev", path: "https://zenpostapp.denisbitter.de" },
    { name: "SagHallo", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < lastScrollY.current) {
        setShowTopBar(true);
      } else {
        setShowTopBar(false);
      }
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toSearchText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) return value.map(toSearchText).join(" ");
    if (typeof value === "object") return Object.values(value).map(toSearchText).join(" ");
    return "";
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    const query = toSearchText(value).trim().toLowerCase();

    if (query.length > 0) {
      const courseResults = courses.filter((course) =>
        `${toSearchText(course?.title)} ${toSearchText(course?.description)} ${toSearchText(course?.instructor)}`
          .toLowerCase()
          .includes(query)
      );

      const blogResults = blogPosts.filter((b) =>
        `${toSearchText(b?.title)} ${toSearchText(b?.content)} ${toSearchText(b?.excerpt)}`
          .toLowerCase()
          .includes(query)
      );

      setSearchResults({
        courses: courseResults.slice(0, 5),
        blog: blogResults.slice(0, 5),
      });
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  useEffect(() => {
    if (!navbarRef.current || !updateNavbarHeight) return;
    const node = navbarRef.current;
    const syncHeight = () => {
      const height = node.offsetHeight;
      updateNavbarHeight(height);
      document.documentElement.style.setProperty("--navbar-height", `${height}px`);
    };
    syncHeight();
    const observer = new ResizeObserver(() => syncHeight());
    observer.observe(node);
    return () => observer.disconnect();
  }, [updateNavbarHeight]);

  useEffect(() => {
    if (!isMobileSearchOpen) return;
    scrollPosition.current = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollPosition.current}px`;
    document.body.style.width = "100%";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.position = "relative";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.position = "";
      window.scrollTo(0, scrollPosition.current);
    };
  }, [isMobileSearchOpen]);

  useEffect(() => {
    const handleAnalysisState = (e) => {
      setIsAnalysisOpen(Boolean(e.detail?.isOpen));
    };
    window.addEventListener("strategic-analysis-state", handleAnalysisState);
    return () => window.removeEventListener("strategic-analysis-state", handleAnalysisState);
  }, []);

  return (
    <header
      ref={navbarRef}
      className="fixed top-[0] w-full 
      z-[80] border-b border-gray-400/50
       bg-[#d9d4c5] shadow-md"
    >
      {/* Top Bar */}
      <PaperGradientBG 
      variant="zenBlack"
      className="py-2 text-[#d0cbb8] hidden lg:flex justify-between px-4">
        <div className="flex items-center space-x-4 px-36">
          <a href={socialMediaLinks.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaLinkedin size={15}/></a>
          <a href={socialMediaLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaInstagram size={15}/></a>
          <a href={socialMediaLinks.github} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaGithub size={15}/></a>
          <a href={socialMediaLinks.stack} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaStackOverflow size={15}/></a>
        </div>

        <div className="flex items-center space-x-4 text-xs hover:text-brand">
          
          <button onClick={() => { setBookingContext("intro"); setShowBooking(true); }}>lern mich kennen</button>
          {showBooking && (
            <Suspense fallback={null}>
              <BookingModal context={bookingContext} onClose={() => setShowBooking(false)} />
            </Suspense>
          )}
        </div>

        <div className="flex items-center space-x-4 text-xs font-mono">
          <a href={`mailto:${mail.value}`} className="mt-1 hidden sm:block hover:text-brand">
            {mail.text}
          </a>
          <a href={`tel:${telNummer.value}`} className="mt-1 hidden sm:block hover:text-brand">
            {telNummer.text}
          </a>
          {token ? (
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
            >
              <MonoButtonSmall className="text-xs bg-brand text-gray-900 h-5">Log Out</MonoButtonSmall>
            </button>
          ) : (
            <Link to="/login">
              <MonoButtonSmall className="text-xs bg-brand h-5">{loginName.text}</MonoButtonSmall>
            </Link>
          )}
        </div>
      </PaperGradientBG>

      {/* Main Navbar */}
      <nav className="font-mono">
        <div className="container mx-auto flex justify-between items-center px-4 py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <Link
              to="/"
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center lg:flex border border-brand/40 bg-transparent"
              aria-label="Zur Startseite"
            >
              <img
                src={logoBackPath}
                alt="Logo"
                className="w-full h-full rounded-full object-contain"
                width="512"
                height="512"
                decoding="async"
              />
            </Link>
<span
  className="
    hidden                 /* Standard: versteckt */
    lg:inline-block        /* erst ab lg sichtbar */
    font-mono text-xs text-gray-900 whitespace-nowrap
    overflow-hidden text-ellipsis
    lg:max-w-none
  "
>
  Software Systems Engineer
</span>
            {!isMobileSearchOpen && (
              <div className="flex items-center space-x-3 text-[#171717] lg:hidden">
                <SocialIconButton href={socialMediaLinks.linkedin} Icon={FaLinkedin} />
         
                <SocialIconButton href={socialMediaLinks.instagram} Icon={FaInstagram} />
                <SocialIconButton href={socialMediaLinks.github} Icon={FaGithub} />
               
              </div>
            )}
          </div>

          {/* Desktop Links */}
          <div className="hidden xl:flex items-center space-x-6">
            {navLinks.map((link) => {
              const isExternal = /^https?:\/\//.test(link.path);
              const isActive = link.isAnchor ? location.pathname === "/" : location.pathname === link.path;
              if (link.isAnchor) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    className={`relative text-xs group ${isActive ? "text-black" : "text-[#1a1a1a] hover:text-black"}`}
                  >
                    {link.name}
                    <span className="absolute inset-x-0 -bottom-3 h-[8px] pointer-events-none">
                      <div
                        className={`w-full transition-transform duration-500 ease-in-out origin-left transform ${
                          isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                        }`}
                      >
                        <UnderlineSketch color={isActive ? "black" : "#AC8E66"} />
                      </div>
                    </span>
                  </a>
                );
              }
              if (isExternal) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`relative text-xs group ${isActive ? "text-black" : "text-[#1a1a1a] hover:text-black"}`}
                  >
                    {link.name}
                    <span className="absolute inset-x-0 -bottom-3 h-[8px] pointer-events-none">
                      <div
                        className={`w-full transition-transform duration-500 ease-in-out origin-left transform ${
                          isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                        }`}
                      >
                        <UnderlineSketch color={isActive ? "black" : "#AC8E66"} />
                      </div>
                    </span>
                  </a>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-xs group ${isActive ? "text-black" : "text-[#1a1a1a] hover:text-black"}`}
                >
                  {link.name}
                  <span className="absolute inset-x-0 -bottom-3 h-[8px] pointer-events-none">
                    <div
                      className={`w-full transition-transform duration-500 ease-in-out origin-left transform ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    >
                      <UnderlineSketch color={isActive ? "black" : "#AC8E66"} />
                    </div>
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center ml-6 gap-3">
            <button
              type="button"
              onClick={scrollToAnalysis}
              className="strategic-button inline-flex items-center border border-[#1a1a1a]/40 rounded-md px-4 py-1.5 text-xs tracking-[0.08em] text-[#1a1a1a] hover:bg-[#cfc8b7] transition-colors"
            >
              {isAnalysisOpen ? "[ Analyse läuft ]" : "[ Analyse starten ]"}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setBookingContext("qa"); setShowBooking(true); }}
                className="strategic-button inline-flex items-center border border-[#1a1a1a]/40 rounded-md px-4 py-1.5 text-xs tracking-[0.08em] text-[#1a1a1a] hover:bg-[#cfc8b7] transition-colors"
              >
                [ Software Q&A ]
              </button>
            </div>
          </div>

          {/* Mobile CTA */}
          <div className="lg:hidden flex items-center">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={scrollToAnalysis}
                className="text-gray-800 pt-1 text-xs tracking-[0.08em]"
              >
                {isAnalysisOpen ? "[ Läuft ]" : "[ Analyse ]"}
              </button>
              <button
                type="button"
                onClick={() => { setBookingContext("qa"); setShowBooking(true); }}
                className="text-gray-800 pt-1 text-xs tracking-[0.08em]"
              >
                [ Q&A ]
              </button>
            </div>
          </div>
        </div>
      </nav>

      {(isMobileSearchOpen || isDesktopSearchOverlayOpen) && (
        <SearchOverlay
          initialView={searchOverlayInitialView}
          searchQuery={searchQuery}
          filteredCourses={searchResults.courses}
          filteredBlog={searchResults.blog}
          onSearchChange={handleSearchChange}
          onResultClick={() => setShowSearchResults(false)}
          onClose={() => {
            setIsMobileSearchOpen(false);
            setIsDesktopSearchOverlayOpen(false);
            setSearchOverlayInitialView("search");
          }}
        />
      )}

      {/* Sekundär-Navigation für Seiten mit Ankern */}
      {pageAnchors[location.pathname] && (() => {
        const config = pageAnchors[location.pathname];
        const isLernstudio = config.useQuery;

        return (
          <div className="w-full bg-[#efefef] border-b border-gray-300 py-2 shadow-md">
            <div className="container mx-auto px-3">
              <div className="flex flex-col md:flex-row md:flex-wrap justify-center items-center gap-2 font-mono text-xs">
              {config.tabs.map((item) => {
                // Für Lernstudio (useQuery: true)
                if (isLernstudio) {
                  const params = new URLSearchParams(location.search);
                  const activeTab = params.get("tab") || "zendev";
                  const isActive = activeTab === item;

                  return (
                    <Link key={item} to={`${location.pathname}?tab=${item}`} className="w-full md:w-auto">
                      <MonoButton
                        className={`w-full py-2 transition text-center ${
                          isActive
                            ? " bg-[#151515] text-orange-50 text-shadow"
                            : "text-gray-950"
                        }`}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </MonoButton>
                    </Link>
                  );
                }

                // Für andere Seiten (useQuery: false) - Anchor Links
                const handleAnchorClick = (e) => {
                  e.preventDefault();
                  const element = document.getElementById(item.id);
                  if (element) {
                    const navbarHeight = navbarRef.current?.offsetHeight || 120;
                    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                    const offsetPosition = elementPosition - navbarHeight - 20;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                };

                // Prüfe ob dieser Anker aktuell sichtbar ist (für active State)
                const isActive = location.hash === `#${item.id}`;

                return (
                  <MonoButton
                    key={item.id}
                    onClick={handleAnchorClick}
                    className={`w-full md:w-auto py-2 transition text-center ${
                      isActive
                        ? " bg-[#151515] text-orange-50 text-shadow"
                        : "text-gray-950"
                    }`}
                  >
                    {item.label}
                  </MonoButton>
                );
              })}
              </div>
            </div>
          </div>
        );
      })()}
    </header>
  );
}

export default Navbar;
